import React, { useEffect, useState } from "react";
import { Wallet } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, StatCard, Money, Badge, statusTone, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";

export default function SellerWallet() {
  const [dash, setDash] = useState(null);
  const [txns, setTxns] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);

  const load = () => {
    api.get("/seller/dashboard").then(({ data }) => setDash(data));
    api.get("/seller/transactions").then(({ data }) => setTxns(data));
    api.get("/my/withdrawals").then(({ data }) => setWithdrawals(data));
  };
  useEffect(() => { load(); }, []);
  if (!dash) return null;

  return (
    <div data-testid="seller-wallet">
      <PageHeader title="Commission Wallet" subtitle="Track earnings and request withdrawals" action={<WithdrawDialog available={dash.available_commission} onDone={load} />} />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
        <StatCard label="Total Earned" value={<Money amount={dash.total_commission} />} icon={Wallet} />
        <StatCard label="Available" value={<Money amount={dash.available_commission} />} icon={Wallet} accent="bg-emerald-600" />
        <StatCard label="Pending" value={<Money amount={dash.pending_commission} />} icon={Wallet} accent="bg-amber" />
        <StatCard label="Withdrawn" value={<Money amount={dash.withdrawn_commission} />} icon={Wallet} accent="bg-navy" />
      </div>

      <Tabs defaultValue="txns">
        <TabsList className="mb-4"><TabsTrigger value="txns">Transactions</TabsTrigger><TabsTrigger value="withdrawals">Withdrawals</TabsTrigger></TabsList>
        <TabsContent value="txns">
          {txns.length === 0 ? <EmptyState icon={Wallet} title="No transactions" /> : (
            <div className="bg-white border border-border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted text-left text-muted-foreground"><tr><th className="p-4">Course</th><th className="p-4">Sale</th><th className="p-4">Commission</th><th className="p-4">Date</th><th className="p-4">Status</th></tr></thead>
                <tbody>{txns.map((t) => (
                  <tr key={t.id} className="border-t border-border"><td className="p-4 font-medium text-navy">{t.course_title}</td><td className="p-4"><Money amount={t.sale_amount} /></td><td className="p-4 font-semibold text-emerald-600"><Money amount={t.amount} /></td><td className="p-4 text-muted-foreground">{new Date(t.created_at).toLocaleDateString()}</td><td className="p-4"><Badge tone={statusTone(t.status)}>{t.status}</Badge></td></tr>
                ))}</tbody>
              </table>
            </div>
          )}
        </TabsContent>
        <TabsContent value="withdrawals">
          {withdrawals.length === 0 ? <EmptyState icon={Wallet} title="No withdrawal requests" /> : (
            <div className="bg-white border border-border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted text-left text-muted-foreground"><tr><th className="p-4">Amount</th><th className="p-4">Method</th><th className="p-4">Date</th><th className="p-4">Status</th></tr></thead>
                <tbody>{withdrawals.map((w) => (
                  <tr key={w.id} className="border-t border-border"><td className="p-4 font-semibold"><Money amount={w.amount} /></td><td className="p-4 capitalize">{w.method}</td><td className="p-4 text-muted-foreground">{new Date(w.created_at).toLocaleDateString()}</td><td className="p-4"><Badge tone={statusTone(w.status)}>{w.status}</Badge></td></tr>
                ))}</tbody>
              </table>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function WithdrawDialog({ available, onDone }) {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [upi, setUpi] = useState("");
  const submit = async () => {
    try { await api.post("/withdrawals", { amount: parseFloat(amount), method: "upi", details: { upi } }); toast.success("Withdrawal requested"); setOpen(false); onDone(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="viriks-navy text-white" data-testid="withdraw-btn">Request Withdrawal</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Request Withdrawal</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Available balance: <Money amount={available} className="font-semibold text-navy" /> • Min ₹500</p>
        <div className="space-y-3 mt-2">
          <div><Label>Amount (₹)</Label><Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="withdraw-amount" /></div>
          <div><Label>UPI ID</Label><Input value={upi} onChange={(e) => setUpi(e.target.value)} placeholder="name@bank" data-testid="withdraw-upi" /></div>
        </div>
        <DialogFooter><Button className="viriks-navy text-white" onClick={submit} data-testid="submit-withdraw">Submit Request</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
